import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { Game } from '../../../models/communication/app-state.model';
import { ButtonGroupComponent, ButtonInGroup } from '../../button-group/button-group.component';
import { PaginationComponent } from '../../pagination/pagination.component';

/**
 * Component for selecting a game from a list of available games.
 *
 * This component provides functionality for filtering games based on their type
 * (e.g., virtual or live), paginating the list of games, and emitting events
 * when a game is selected or the back button is clicked.
 */
@Component({
    selector: 'app-game-selection',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonGroupComponent, PaginationComponent],

    templateUrl: './game-selection.component.html',
    styleUrl: './game-selection.component.scss'
})
export class GameSelectionComponent implements OnInit {
    static readonly gamesPerPage = 6;

    @Input()
    title = $localize`Select game`;

    @Input()
    games?: Game[];

    @Input()
    gamesPerPage: number = GameSelectionComponent.gamesPerPage;

    @Input()
    currentGameId?: Game['id'];

    @Input()
    showBackButton = true;

    @Output()
    readonly gameSelected = new EventEmitter<Game['id']>();

    @Output()
    readonly backClicked = new EventEmitter();

    protected filteredGames: Game[] = [];

    // Virtual toggle buttons
    protected virtualToggleButtonVisible = false;
    protected virtualToggleButtons!: ButtonInGroup[];

    // Pagination
    protected pageIndex = 0;
    protected pagesCount = 0;
    protected filteredGamesPerPage: Game[] = [];

    ngOnInit(): void {
        if (!this.games) {
            // eslint-disable-next-line no-console
            console.error('No available games');
            return;
        }

        const liveButton: ButtonInGroup = {
            label: $localize`Live`,
            action: this.isNotVirtual
        };

        const virtualButton: ButtonInGroup = {
            label: $localize`Virtual`,
            action: this.isVirtual
        };

        this.virtualToggleButtons = [liveButton, virtualButton];

        // If the games are mixed virtual and non virtual, init virtual toggle buttons
        if (this.games.some(this.isVirtual) && this.games.some(this.isNotVirtual)) {
            const currentGame = this.games.find((game) => game.id === this.currentGameId);
            const selectedButton = currentGame && this.isVirtual(currentGame) ? virtualButton : liveButton;
            this.initVirtualToggleButtons(selectedButton);
            this.filterGames(selectedButton.action);
        } else {
            this.filterGames(this.games.every(this.isVirtual) ? this.isVirtual : this.isNotVirtual);
        }
    }

    /**
     * Filters the list of available games based on the provided function.
     */
    protected filterGames(predicateFn: ButtonInGroup['action']): void {
        this.filteredGames = this.games?.filter(predicateFn) || [];
        this.pagesCount = Math.ceil(this.filteredGames.length / this.gamesPerPage);
        this.pageIndex = 0;
        this.updateFilteredGamesPerPage();
    }

    /**
     * Updates the index and the filtered games displayed per page.
     */
    protected updatePageIndex(pageIndex: number): void {
        this.pageIndex = pageIndex;
        this.updateFilteredGamesPerPage();
    }

    /**
     * Updates the `filteredGamesPerPage` as a subset of `filteredGames`
     * based on the current `pageIndex` and `gamesPerPage`.
     */
    private updateFilteredGamesPerPage(): void {
        this.filteredGamesPerPage = this.filteredGames.slice(this.pageIndex * this.gamesPerPage, (this.pageIndex + 1) * this.gamesPerPage);
    }

    /**
     * Determines if the `currentGame` is virtual and updates the view model accordingly.
     */
    private initVirtualToggleButtons(selectedButton: ButtonInGroup): void {
        for (const button of this.virtualToggleButtons) {
            button.initiallySelected = button === selectedButton;
        }

        this.virtualToggleButtonVisible = true;
    }

    /**
     * Determines if the given `game` is virtual.
     *
     * An arrow function is used to avoid problems with referencing
     * unbound methods which may cause unintentional scoping of `this`.
     */
    private readonly isVirtual = (game: Game): boolean => game.isVirtual;

    /**
     * A helper - negation of `isVirtual`
     */
    private readonly isNotVirtual = (game: Game): boolean => !this.isVirtual(game);
}
